import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import SearchSelect from "../../components/SearchSelect";
import { useDocumentChangeAddress, useDraftRetriever, useDistrict, useRegion, useStreet } from "../../api/DataAPI";
import type { ChangeAddressFormValues } from "../../api/DataAPI";
import useStrings from "../../api/StringsAPI";

interface Props {
  visible: boolean;
  onHide: () => void;
  documentId: number | null;
  onSuccess: () => void;
}

export default function ChangeAddressDialog({ visible, onHide, documentId, onSuccess }: Props) {
  const str = useStrings();
  const query = useDraftRetriever(documentId);
  const changeAddressMutation = useDocumentChangeAddress();
  const regionQuery = useRegion();

  const [form, setForm] = useState({
    region: '' as string | number,
    district: '' as string | number,
    street: '' as string | number,
    addressAdditional: '',
    phone: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const districtQuery = useDistrict(form.region as number);
  const streetQuery = useStreet(form.district as string);

  useEffect(() => {
    if (query.data?.data) {
      const doc = query.data.data;
      setForm({
        region: doc.region?.id || '',
        district: doc.district?.id || '',
        street: doc.street?.id || '',
        addressAdditional: doc.addressAdditional || '',
        phone: doc.phone || '',
      });
    }
    setErrors({});
    setMessage(null);
  }, [query.data, visible]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.region) newErrors.region = str('requiredField');
    if (!form.district) newErrors.district = str('requiredField');
    if (!form.street) newErrors.street = str('requiredField');
    if (!form.addressAdditional) newErrors.addressAdditional = str('requiredField');
    if (!form.phone) newErrors.phone = str('requiredField');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (documentId === null || !validate()) return;
    setMessage(null);

    const data: ChangeAddressFormValues = {
      id: documentId,
      region: form.region,
      district: form.district,
      street: form.street,
      addressAdditional: form.addressAdditional,
      phone: form.phone,
    };

    changeAddressMutation.mutate(data, {
      onSuccess: () => {
        setMessage({ type: 'success', text: str('changeAddressSuccess') });
        onSuccess();
        setTimeout(onHide, 1500);
      },
      onError: () => {
        setMessage({ type: 'error', text: str('changeAddressError') });
      }
    });
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      <Button label={str('cancel')} severity="secondary" onClick={onHide} />
      <Button label={str('changeAddress')} icon="pi pi-check" onClick={handleSubmit} loading={changeAddressMutation.isLoading} />
    </div>
  );

  return (
    <Dialog header={str('changeAddress')} visible={visible} onHide={onHide} footer={footer} style={{ width: '600px' }} modal>
      {query.isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
          <ProgressSpinner />
        </div>
      )}

      {query.isSuccess && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {/* Row 1: Region & District */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label>{str('region')}</label>
              <SearchSelect
                value={form.region}
                options={regionQuery.data?.data}
                onChange={(value) => setForm({ ...form, region: value, district: '', street: '' })}
                optionLabel="name"
                optionValue="id"
                className={errors.region ? 'p-invalid' : ''}
              />
              {errors.region && <small className="p-error">{errors.region}</small>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label>{str('district')}</label>
              <SearchSelect
                value={form.district}
                options={districtQuery.data?.data}
                onChange={(value) => setForm({ ...form, district: value, street: '' })}
                optionLabel="name"
                optionValue="id"
                disabled={!form.region}
                className={errors.district ? 'p-invalid' : ''}
              />
              {errors.district && <small className="p-error">{errors.district}</small>}
            </div>
          </div>

          {/* Row 2: Street & Address Additional */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label>{str('street')}</label>
              <SearchSelect
                value={form.street}
                options={streetQuery.data?.data}
                onChange={(value) => setForm({ ...form, street: value })}
                optionLabel="name"
                optionValue="id"
                disabled={!form.district}
                className={errors.street ? 'p-invalid' : ''}
              />
              {errors.street && <small className="p-error">{errors.street}</small>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label>{str('addressAdditional')}</label>
              <InputText
                value={form.addressAdditional}
                onChange={(e) => setForm({ ...form, addressAdditional: e.target.value })}
                className={errors.addressAdditional ? 'p-invalid' : ''}
              />
              {errors.addressAdditional && <small className="p-error">{errors.addressAdditional}</small>}
            </div>
          </div>

          {/* Row 3: Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label>{str('phone')}</label>
              <InputText
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={errors.phone ? 'p-invalid' : ''}
              />
              {errors.phone && <small className="p-error">{errors.phone}</small>}
            </div>
          </div>

          {message && (
            <Message severity={message.type} text={message.text} />
          )}
        </div>
      )}
    </Dialog>
  );
}
